import React, { useState } from 'react';
import { Box, Typography, Button, Radio, Grid, Card, CardContent, FormHelperText } from '@mui/material';
import { PlayArrow, VolumeUp, VolumeOff, MusicNote, AccessAlarm } from '@mui/icons-material';
import { toast } from 'react-toastify';

const soundOptions = [
    {
        id: 'double-beep',
        name: 'Double Beep (Urgent)',
        description: 'Standard double-beep high pitch digital alarm pattern',
        icon: <AccessAlarm color="error" />
    },
    {
        id: 'slow-pulse',
        name: 'Slow Warning Pulse',
        description: 'Deep, pulsating low tone suitable for quieter environments',
        icon: <VolumeUp color="primary" />
    },
    {
        id: 'high-siren',
        name: 'High Siren Alert',
        description: 'Rapid, piercing frequency sweep for noisy rooms',
        icon: <MusicNote color="warning" />
    },
    {
        id: 'digital-chime',
        name: 'Digital Chime',
        description: 'Melodic three-note chime sequence',
        icon: <VolumeUp color="success" />
    },
    {
        id: 'mute',
        name: 'Silent (Visual Only)',
        description: 'Dashboard screen flashes red with no alert audio playing',
        icon: <VolumeOff color="action" />
    }
];

const playAlertSound = (type) => {
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const now = audioCtx.currentTime;

        if (type === 'double-beep') {
            // Beep 1
            const osc1a = audioCtx.createOscillator();
            const osc1b = audioCtx.createOscillator();
            const gain1 = audioCtx.createGain();
            osc1a.connect(gain1);
            osc1b.connect(gain1);
            gain1.connect(audioCtx.destination);

            osc1a.frequency.setValueAtTime(880, now);
            osc1b.frequency.setValueAtTime(440, now);
            gain1.gain.setValueAtTime(0.5, now);
            gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
            osc1a.start(now);
            osc1b.start(now);
            osc1a.stop(now + 0.15);
            osc1b.stop(now + 0.15);

            // Beep 2
            const osc2a = audioCtx.createOscillator();
            const osc2b = audioCtx.createOscillator();
            const gain2 = audioCtx.createGain();
            osc2a.connect(gain2);
            osc2b.connect(gain2);
            gain2.connect(audioCtx.destination);

            osc2a.frequency.setValueAtTime(880, now + 0.18);
            osc2b.frequency.setValueAtTime(440, now + 0.18);
            gain2.gain.setValueAtTime(0.5, now + 0.18);
            gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.33);
            osc2a.start(now + 0.18);
            osc2b.start(now + 0.18);
            osc2a.stop(now + 0.33);
            osc2b.stop(now + 0.33);
        } else if (type === 'slow-pulse') {
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            osc.connect(gain);
            gain.connect(audioCtx.destination);

            osc.type = 'triangle';
            osc.frequency.setValueAtTime(330, now);
            gain.gain.setValueAtTime(0.7, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
            osc.start(now);
            osc.stop(now + 0.4);
        } else if (type === 'high-siren') {
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            osc.connect(gain);
            gain.connect(audioCtx.destination);

            osc.frequency.setValueAtTime(988, now);
            osc.frequency.linearRampToValueAtTime(1318, now + 0.15);
            gain.gain.setValueAtTime(0.5, now);
            gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
            osc.start(now);
            osc.stop(now + 0.15);
        } else if (type === 'digital-chime') {
            [523.25, 659.25, 783.99].forEach((freq, index) => {
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();
                osc.connect(gain);
                gain.connect(audioCtx.destination);

                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, now + index * 0.1);
                gain.gain.setValueAtTime(0.4, now + index * 0.1);
                gain.gain.exponentialRampToValueAtTime(0.01, now + index * 0.1 + 0.15);
                osc.start(now + index * 0.1);
                osc.stop(now + index * 0.1 + 0.15);
            });
        }
    } catch (e) {
        console.error('Test play failed:', e);
    }
};

const VendorAlertSoundSettings = () => {
    const [selectedSound, setSelectedSound] = useState(
        localStorage.getItem('vendorOrderAlertSound') || 'double-beep'
    );

    const handleSave = () => {
        localStorage.setItem('vendorOrderAlertSound', selectedSound);
        toast.success('Alert sound preference saved!');
    };

    return (
        <Box sx={{ maxWidth: 700, m: '0 auto', p: 2 }}>
            <Box mb={4}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Order Alert Sound Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Configure the audio alarm style for new incoming orders. Choose a pattern below, click the play button to preview it, and save your selection.
                </Typography>
            </Box>

            <Grid container spacing={2}>
                {soundOptions.map((option) => (
                    <Grid item xs={12} key={option.id}>
                        <Card 
                            variant="outlined" 
                            sx={{ 
                                borderColor: selectedSound === option.id ? 'primary.main' : '#eee',
                                borderWidth: selectedSound === option.id ? 2 : 1,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: selectedSound === option.id ? 'primary.main' : '#ccc'
                                }
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '16px !important' }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Radio 
                                        checked={selectedSound === option.id}
                                        onChange={() => setSelectedSound(option.id)}
                                        value={option.id}
                                        name="alert-sound-radio"
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '50%', backgroundColor: '#f5f5f5' }}>
                                        {option.icon}
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {option.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {option.description}
                                        </Typography>
                                    </Box>
                                </Box>

                                {option.id !== 'mute' && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PlayArrow />}
                                        onClick={() => playAlertSound(option.id)}
                                        sx={{ textTransform: 'none', borderRadius: '6px' }}
                                    >
                                        Play Test
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Box mt={4} display="flex" justifyContent="flex-end">
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSave}
                    sx={{ textTransform: 'none', borderRadius: '8px', px: 4, py: 1.2, fontWeight: 'bold' }}
                >
                    Save Preferences
                </Button>
            </Box>
        </Box>
    );
};

export default VendorAlertSoundSettings;
